import type { ReactNode } from 'react'
import { Badge, List, Row, Section } from '@/components/ui'

export type ReviewRow = {
  id: number
  title: string
  sub?: string
  pts?: string
  onApprove: () => void
  approveLabel: string
  /** 不给就只有一个「确认」按钮 —— 作业的签名没有「驳回」这个动作。 */
  onReject?: () => void
  /** 按钮上的可见文案（「驳回」）。 */
  rejectLabel?: string
  /** 无障碍名称。必须带上是哪一条 —— 屏幕阅读器逐个读按钮时，
      三个都叫「驳回」等于三个一样的按钮。 */
  rejectAriaLabel?: string
}

/**
 * 「等我确认」队列。**两个使用者共用同一个界面**：
 *   * 打卡页的「等我确认的申报」（TaskClaim）
 *   * 作业页的「等签名」段（HomeworkItem state=pending）
 *
 * 为什么必须共用：家长睡前要处理的是**一个**队列，不是两个
 * （作业设计稿 §六② + UI 重构交接稿 §8）。各做一份必然长歪成两种样子。
 */
export default function ReviewQueue({ title, count, hint, rows }: {
  title: ReactNode
  count: number
  hint?: string
  rows: ReviewRow[]
}) {
  if (rows.length === 0) return null

  return (
    <Section title={title} tail={<Badge tone="amber">{count}</Badge>}>
      <List>
        {rows.map((r) => (
          <Row
            key={r.id}
            lead="✋"
            title={r.title}
            sub={r.sub}
            pts={r.pts}
            right={
              <span className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={r.onApprove}
                  aria-label={r.approveLabel}
                  className="min-w-tap min-h-tap rounded-chip bg-green-tint text-green-strong text-base font-bold active:bg-green active:text-white"
                >
                  ✓
                </button>
                {r.onReject && (
                  <button
                    type="button"
                    onClick={r.onReject}
                    aria-label={r.rejectAriaLabel ?? r.rejectLabel}
                    className="min-w-tap min-h-tap rounded-chip text-muted text-[13px] font-semibold active:bg-sunk active:text-red"
                  >
                    {r.rejectLabel}
                  </button>
                )}
              </span>
            }
          />
        ))}
      </List>
      {hint && <p className="mt-2 px-1 text-faint text-xs">{hint}</p>}
    </Section>
  )
}
